import {Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {SupportAiConfig, SupportAiConversation, SupportAiHandoff, SupportAiMessage, SupportAiScope} from '../../models/support-ai';
import {SupportAiService} from '../../services/support-ai.service';

@Component({
	selector: 'app-support-ai',
	templateUrl: './support-ai.component.html',
	styleUrls: ['./support-ai.component.css']
})
export class SupportAiComponent implements OnChanges, OnDestroy {
	@ViewChild('messagesContainer') messagesContainer: ElementRef<HTMLElement>;
	@Input() open = false;
	@Input() humanHandoffEnabled = false;
	@Input() config: SupportAiConfig;
	@Output() openChange = new EventEmitter<boolean>();
	@Output() handoffConfirmed = new EventEmitter<SupportAiHandoff>();

	conversations: SupportAiConversation[] = [];
	activeConversation: SupportAiConversation;
	messages: SupportAiMessage[] = [];
	draft = '';
	status = '';
	loading = false;
	error = '';
	selectedScope: SupportAiScope = 'team';
	readonly adminScopes: SupportAiScope[] = ['admin', 'team'];
	private abortController: AbortController;
	private scrollFrame: number;

	constructor(@Inject(SupportAiService) private supportAiService: SupportAiService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.config?.currentValue && !this.activeConversation) this.selectedScope = this.config.defaultScope;
		if (changes.open?.currentValue) this.initialize();
		if (changes.open && !changes.open.currentValue) this.abort();
	}

	ngOnDestroy(): void {
		this.abort();
		if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
	}

	close(): void {
		this.open = false;
		this.openChange.emit(false);
		this.abort();
	}

	async initialize(): Promise<void> {
		if (this.loading || this.conversations.length) return;
		this.loading = true;
		this.error = '';
		try {
			this.conversations = await this.supportAiService.listConversations();
			const conversation = this.conversations.find(item => item.scope === this.selectedScope);
			if (conversation) await this.selectConversation(conversation);
			else await this.newConversation();
		}
		catch (err: any) { this.error = err?.message || 'Support AI is temporarily unavailable'; }
		finally { this.loading = false; }
	}

	async newConversation(): Promise<void> {
		this.abort();
		this.activeConversation = await this.supportAiService.createConversation(this.selectedScope);
		this.conversations.unshift(this.activeConversation);
		this.messages = [];
		this.error = '';
	}

	async selectConversation(conversation: SupportAiConversation): Promise<void> {
		this.abort();
		this.activeConversation = conversation;
		this.selectedScope = conversation.scope;
		this.loading = true;
		try {
			this.messages = (await this.supportAiService.getMessages(conversation._id)).reverse();
			this.scrollToLatestMessage();
		}
		catch (err: any) { this.error = err?.message || 'Unable to load this conversation'; }
		finally { this.loading = false; }
	}

	async changeScope(scope: SupportAiScope): Promise<void> {
		if (scope === this.selectedScope || !this.config?.availableScopes?.includes(scope)) return;
		this.abort();
		this.selectedScope = scope;
		const conversation = this.conversations.find(item => item.scope === scope);
		if (conversation) await this.selectConversation(conversation);
		else await this.newConversation();
	}

	scopeLabel(scope: SupportAiScope): string {
		return scope === 'admin' ? 'Admin' : scope === 'fan' ? 'Fan' : 'Team';
	}

	emptyStateDescription(): string {
		if (this.selectedScope === 'admin') return 'Ask about cross-team accounts, product behavior, or operational status.';
		if (this.selectedScope === 'fan') return 'Ask about subscriptions, purchases, followed teams, access, or playback.';
		return "Ask how QwikCut works or check the status of your team's authorized resources.";
	}

	async removeConversation(conversation: SupportAiConversation, event: Event): Promise<void> {
		event.stopPropagation();
		if (!confirm('Delete this support conversation?')) return;
		await this.supportAiService.deleteConversation(conversation._id);
		this.conversations = this.conversations.filter(item => item._id !== conversation._id);
		if (this.activeConversation?._id === conversation._id) {
			this.activeConversation = null;
			this.messages = [];
			if (this.conversations.length) await this.selectConversation(this.conversations[0]);
			else await this.newConversation();
		}
	}

	async send(): Promise<void> {
		const message = this.draft.trim();
		if (!message || message.length > 4000 || this.loading || !this.activeConversation) return;
		this.draft = '';
		this.error = '';
		this.loading = true;
		this.status = 'Starting a secure support check';
		this.messages.push({role: 'user', content: message});
		this.scrollToLatestMessage();
		this.abortController = new AbortController();
		try {
			const requestId = `${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
			const answer = await this.supportAiService.sendMessage(
				this.activeConversation._id,
				message,
				requestId,
				status => this.status = status,
				this.abortController.signal
			);
			this.messages.push({
				_id: answer.messageId,
				role: 'assistant',
				content: answer.answer,
				confidence: answer.confidence,
				suggestedActions: answer.suggestedActions,
				evidence: answer.evidence,
				needsHuman: answer.needsHuman
			});
			this.scrollToLatestMessage();
			this.activeConversation.messageCount = (this.activeConversation.messageCount || 0) + 2;
			this.activeConversation.title = this.activeConversation.title === 'Support conversation' ? message.slice(0, 80) : this.activeConversation.title;
		}
		catch (err: any) {
			if (err?.name !== 'AbortError') this.error = err?.message || 'Support AI is temporarily unavailable';
		}
		finally { this.loading = false; this.status = ''; }
	}

	async rate(message: SupportAiMessage, rating: 'up' | 'down'): Promise<void> {
		if (!message._id) return;
		await this.supportAiService.feedback(this.activeConversation._id, message._id, rating).catch(() => undefined);
	}

	async contactHuman(): Promise<void> {
		if (!this.activeConversation || this.loading) return;
		this.loading = true;
		try {
			const handoff = await this.supportAiService.handoffSummary(this.activeConversation._id);
			const confirmed = confirm(`This summary will be sent to QwikCut Support:\n\n${handoff.summary}\n\nContinue to live chat?`);
			if (confirmed) {
				this.handoffConfirmed.emit(handoff);
				this.close();
			}
		}
		catch (err: any) { this.error = err?.message || 'Human support handoff is unavailable'; }
		finally { this.loading = false; }
	}

	onKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); this.send(); }
	}

	private abort(): void {
		this.abortController?.abort();
		this.abortController = null;
	}

	private scrollToLatestMessage(): void {
		if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
		this.scrollFrame = requestAnimationFrame(() => {
			this.scrollFrame = null;
			const container = this.messagesContainer?.nativeElement;
			const latestMessage = container?.querySelector<HTMLElement>('.message:last-of-type');
			if (!container || !latestMessage) return;

			if (latestMessage.offsetHeight > container.clientHeight) {
				const containerTop = container.getBoundingClientRect().top;
				const messageTop = latestMessage.getBoundingClientRect().top;
				container.scrollTop += messageTop - containerTop;
				return;
			}

			container.scrollTop = container.scrollHeight;
		});
	}
}
